import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  applyUpdateAndReload,
  checkForUpdate,
  dismissStaleNotice,
  getUpdateState,
  maybeApplyAtLaunch,
  noteUpdateReady,
  publishRegistration,
  startExternalHandoverWatch,
  startUpdateChecks,
  subscribeToUpdates,
} from '@/pwa/updates';
import { isHeld, registerBusyProbe, subscribeHolds } from '@/pwa/busy';
import { getSkewState, setSkewHandler, startSkewReloadWatch, subscribeToSkew } from '@/pwa/versionSkew';
import { getPendingSend, getSendsInFlight } from '@/features/send-undo/pendingSend';
import { useSnackbarShowing } from './snackbarShowing';
import './update-prompt.css';

/**
 * Whether something holds updates (`@/pwa/busy`): a composer open, settings
 * with unsaved edits, an Outbox flush. The ordinary prompt waits for those to
 * end -- someone writing a message has no use for "reload?" -- and comes back,
 * unchanged, on release.
 */
function useHeld(): boolean {
  return useSyncExternalStore(subscribeHolds, isHeld, isHeld);
}

/**
 * Service-worker registration + "a new version is ready" toast.
 *
 * Registration happens here (vite.config.ts sets `injectRegister: null`) so the
 * app shell precache is warmed on first visit and repeat visits paint from
 * cache before any network request. `registerType: 'prompt'` means we never
 * swap the running app out from under someone mid-compose.
 *
 * The registration is published to `@/pwa/updates`, which owns the periodic
 * check and backs the "Check for updates" button in Settings. Both need this
 * registration, and calling `useRegisterSW` twice would mean two of them.
 */

/*
 * Reload hands over to the waiting worker and reloads once it has taken over
 * (`applyUpdateAndReload` in `@/pwa/updates`, shared with Settings' button,
 * documents the signals and the fallback). A fixed two-second wait used to
 * stand in for that and was too short on a phone: the page reloaded under the
 * old worker and the prompt came straight back.
 *
 * Two cases never show the prompt at all (also in `@/pwa/updates`): a new
 * version found as the app launches, before anything has been started, is
 * applied silently, like a native app updating on launch; and a window left
 * behind when another window took the update reloads quietly if idle.
 */
export function UpdatePrompt() {
  // `offlineReady` is deliberately not read. The app shell is cached for
  // offline use silently: "Ready to work offline" was announced on every
  // first visit and read to people as a claim about their mail (which is not
  // all available offline) or as an error. It needed no answer, so it gets no
  // toast. Only a new version -- which does need one -- is announced.
  const {
    needRefresh: [needRefresh, setNeedRefresh],
  } = useRegisterSW({
    onRegisteredSW(url, registration) {
      // (Push subscriptions use this same registration, found by
      // settings/pushDevice.ts; its handlers are public/push-sw.js, imported
      // into the worker.)
      publishRegistration(registration);
      if (import.meta.env.DEV) console.info('[pwa] service worker registered', url, registration);
    },
    // Given, so vite-plugin-pwa never reloads on `controllerchange` itself: it
    // would do so unconditionally, in every window, mid-compose included.
    // `@/pwa/updates` owns every reload.
    onNeedReload() {},
    onRegisterError(error) {
      console.error('[pwa] service worker registration failed', error);
    },
  });

  // A browser only looks for a new worker when it navigates, and an installed
  // app is never navigated. Without this, a deploy reaches an open window only
  // when someone happens to reload it -- which is how a just-deployed panel
  // stayed invisible in a browser that had the app open.
  useEffect(() => startUpdateChecks(), []);
  useEffect(() => startExternalHandoverWatch(), []);
  // Version skew (ADR 0103, `@/pwa/versionSkew`): a deploy seen on the wire
  // asks for the new worker now, and a contract error after it reloads this
  // window once nothing is at stake.
  useEffect(
    () =>
      setSkewHandler(() => {
        void checkForUpdate();
      }),
    [],
  );
  useEffect(() => startSkewReloadWatch(() => applyUpdateAndReload(undefined, 'launch')), []);
  // A send waiting out its Undo window, or on the wire, is work a silent
  // reload would lose (the composer and unsaved settings hold on their own).
  // (A layout effect so it is in place before the one below first runs.)
  useLayoutEffect(
    () => registerBusyProbe(() => getPendingSend() !== undefined || getSendsInFlight() > 0),
    [],
  );

  // Tell Settings what the toast knows, so the button there can offer the
  // reload rather than re-discovering a version already found. And if the app
  // has only just launched, take the new version silently instead. A layout
  // effect, so the prompt is never painted on the way to that reload.
  useLayoutEffect(() => {
    if (!needRefresh) return;
    noteUpdateReady();
    maybeApplyAtLaunch();
  }, [needRefresh]);

  const update = useSyncExternalStore(subscribeToUpdates, getUpdateState, getUpdateState);
  const skew = useSyncExternalStore(subscribeToSkew, getSkewState, getSkewState);
  const [reloading, setReloading] = useState(false);
  const deferred = useSnackbarShowing();
  const held = useHeld();

  const reload = (): void => {
    setReloading(true);
    applyUpdateAndReload();
  };

  // A silent update is on its way to a reload: say nothing.
  if (update.applying === 'launch') return null;

  // This window must reload (version skew after a contract error). No
  // Dismiss, and shown even while something is held: it is the explanation
  // for the reload that will follow once that ends. An Undo still comes first.
  if (skew.hard) {
    if (deferred && !reloading) return null;
    return (
      <div
        className="update-prompt"
        role="status"
        aria-live="polite"
        data-required="true"
        {...(reloading ? { 'data-reloading': 'true' } : {})}
      >
        <span className="update-prompt__text">
          OLS Mail has been updated and this window needs to reload. It will reload once you are done.
        </span>
        <div className="update-prompt__actions">
          <button type="button" className="btn btn--primary" disabled={reloading} onClick={reload}>
            {reloading ? 'Reloading…' : 'Reload now'}
          </button>
        </div>
      </div>
    );
  }

  const showing = needRefresh || update.staleUnderNewWorker;
  // Once Reload is pressed the prompt stays: it is saying "Reloading…", and a
  // snackbar that opened in the meantime is about to be torn down anyway.
  if (!showing || (deferred && !reloading)) return null;
  // The ordinary prompt waits while something is held. Not the one left
  // behind when another window took the update: this page is then running the
  // old build under the new worker, which no longer has its lazy chunks, and
  // that is worth knowing even mid-message.
  if (held && !reloading && !update.staleUnderNewWorker) return null;

  return (
    <div
      className="update-prompt"
      role="status"
      aria-live="polite"
      {...(reloading ? { 'data-reloading': 'true' } : {})}
    >
      <span className="update-prompt__text">A new version of OLS Mail is ready.</span>
      <div className="update-prompt__actions">
        <button type="button" className="btn btn--primary" disabled={reloading} onClick={reload}>
          {reloading ? 'Reloading…' : 'Reload'}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={reloading}
          onClick={() => {
            setNeedRefresh(false);
            dismissStaleNotice();
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
